import { useState } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";

import { createAbbreviation, createGlossaryTerm } from "@/api/terminology";
import type { TerminologyItem } from "@/api/terminology";
import { useToast } from "@/components/toast";

export function CreateTermPanel() {
  const { toast } = useToast();
  const [kind, setKind] = useState<"term" | "abbrev">("term");
  const [en, setEn] = useState("");
  const [ru, setRu] = useState("");
  const [domain, setDomain] = useState("");
  const [abbr, setAbbr] = useState("");
  const [srcExp, setSrcExp] = useState("");
  const [tgtExp, setTgtExp] = useState("");
  const [tgtAbbr, setTgtAbbr] = useState("");
  const [created, setCreated] = useState<TerminologyItem | null>(null);

  const mut = useMutation({
    mutationFn: async () => {
      if (kind === "term") {
        const r = await createGlossaryTerm({
          source_text: en.trim() || undefined,
          target_text: ru.trim() || undefined,
          domain: domain.trim() || undefined,
        });
        if (!r.ok) throw new Error(r.error);
        return r.data.item;
      }
      const r = await createAbbreviation({
        source_abbreviation: abbr.trim(),
        source_expansion: srcExp.trim(),
        target_expansion: tgtExp.trim(),
        target_abbreviation: tgtAbbr.trim() || undefined,
        domain: domain.trim() || undefined,
      });
      if (!r.ok) throw new Error(r.error);
      return r.data.item;
    },
    onSuccess: (item) => {
      toast("Создано", "success");
      setCreated(item);
      setEn("");
      setRu("");
      setAbbr("");
      setSrcExp("");
      setTgtExp("");
      setTgtAbbr("");
    },
    onError: (e: Error) => toast(e.message, "error"),
  });

  const canSubmit =
    kind === "term"
      ? Boolean(en.trim() || ru.trim())
      : Boolean(abbr.trim() && srcExp.trim() && tgtExp.trim());

  const input = (
    placeholder: string,
    value: string,
    set: (v: string) => void,
  ) => (
    <input
      className="mt-input mt-input--sm"
      placeholder={placeholder}
      value={value}
      onChange={(e) => set(e.target.value)}
    />
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
        <button
          type="button"
          className={
            "mt-btn mt-btn--sm " +
            (kind === "term" ? "mt-btn--primary" : "mt-btn--ghost")
          }
          onClick={() => setKind("term")}
        >
          Термин
        </button>
        <button
          type="button"
          className={
            "mt-btn mt-btn--sm " +
            (kind === "abbrev" ? "mt-btn--primary" : "mt-btn--ghost")
          }
          onClick={() => setKind("abbrev")}
        >
          Аббревиатура
        </button>
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
        {kind === "term" ? (
          <>
            {input("EN термин (напр. oral anticoagulants)", en, setEn)}
            {input("RU перевод (напр. оральные антикоагулянты)", ru, setRu)}
            {input("домен (опц., напр. Cardiology)", domain, setDomain)}
          </>
        ) : (
          <>
            {input("EN сокращение (напр. NOAC)", abbr, setAbbr)}
            {input("EN расшифровка (non-vitamin K oral anticoagulant)", srcExp, setSrcExp)}
            {input("RU расшифровка", tgtExp, setTgtExp)}
            {input("RU сокращение (опц., напр. НОАК)", tgtAbbr, setTgtAbbr)}
            {input("домен (опц.)", domain, setDomain)}
          </>
        )}
      </div>
      <div style={{ marginTop: 10 }}>
        <button
          type="button"
          className="mt-btn mt-btn--sm mt-btn--primary"
          disabled={!canSubmit || mut.isPending}
          onClick={() => mut.mutate()}
        >
          Создать
        </button>
      </div>
      {created && (
        <div
          className="rf-example"
          style={{ marginTop: 10, padding: 8, fontSize: "var(--fs-sm)" }}
        >
          Создано: <b>{created.source_text}</b> →{" "}
          <b>{created.target_abbreviation || created.target_text}</b>{" "}
          <Link to={`/admin/terminology/${encodeURIComponent(created.item_id)}`}>
            открыть карточку →
          </Link>
        </div>
      )}
    </div>
  );
}
